import { trapFocus } from "@grrr/utils";
import { publish, subscribe } from "../utils/observer";

const OPEN_POPUP_EVENT = "open-popup";
const CLOSE_POPUP_EVENT = "close-popup";
const FORCE_CLOSE_POPUP_EVENT = "force-close-popup";

// Needed to remove the event listener.
let memoizedClickHandler = null;
// Global var needed to store the focus trap instance.
let focusTrap = null;

/**
 * Force close popup when the escape key is pressed and remove all relevant
 * event listeners.
 */
const forceClose = (event) => {
  if (event.key === "Escape") {
    publish(FORCE_CLOSE_POPUP_EVENT);
    window.removeEventListener("keydown", forceClose);
    document.removeEventListener("click", memoizedClickHandler, {
      capture: true,
    });
  }
};

const trapTheFocus = (element) => {
  focusTrap = trapFocus(element.firstElementChild);
};

const releaseFocus = () => {
  if (focusTrap) {
    focusTrap.release();
    focusTrap = null;
  }
};

/**
 * Update state of popup based on Observer signals.
 */
const updatePopupState =
  (element, bodyElement, { open }) =>
  (id) => {
    const elementPopupId = element.getAttribute("data-popup-id");
    const trigger = document.querySelector(
      `[data-handler="openPopupHandler"][data-popup-id="${elementPopupId}"]`,
    );

    if (elementPopupId === id) {
      element.setAttribute("data-visible", open);
      element.setAttribute("aria-hidden", !open);
    } else {
      element.setAttribute("data-visible", false);
      element.setAttribute("aria-hidden", true);
    }

    const preventFocusTrap = element.getAttribute("data-prevent-focus-trap");
    if (preventFocusTrap !== "true" && open && elementPopupId === id) {
      trapTheFocus(element);
    }
    if (preventFocusTrap !== "true" && !open && elementPopupId === id) {
      releaseFocus();
      trigger?.focus();
    }

    const allowScrollWhenOpen = element.getAttribute(
      "data-allow-scroll-when-opened",
    );
    if (elementPopupId !== id || allowScrollWhenOpen === "true") return;

    bodyElement.setAttribute("popup-is-open", open);
  };

/**
 * Close popup even if the caller doesn't know the popup id.
 */
const handleForceClosePopup = (element, bodyElement) => () => {
  if (element.getAttribute("aria-hidden") === "true") {
    return;
  }

  const elementPopupId = element.getAttribute("data-popup-id");
  const trigger = document.querySelector(
    `[data-handler="openPopupHandler"][data-popup-id="${elementPopupId}"]`,
  );
  element.setAttribute("data-visible", "false");
  element.setAttribute("aria-hidden", "true");

  // Disable scrolling of the page if the popup is open.
  bodyElement.setAttribute("popup-is-open", "false");

  releaseFocus();
  trigger?.focus();
};

/**
 * Close popup if the user clicks outside of the box.
 */
const handleClickOutside = (element) => (event) => {
  const id = element.getAttribute("data-popup-id");
  const targetElement = document.querySelector(
    `[data-enhancer="popupMenu"][data-popup-id="${id}"]`,
  );

  const isClickInside = targetElement.contains(event.target);
  if (isClickInside) return;

  publish(CLOSE_POPUP_EVENT, id);
  document.removeEventListener("click", memoizedClickHandler, {
    capture: true,
  });
};

/**
 * Handler to open the popup.
 */
export const openPopupHandler = (element, event) => {
  // Prevent an immediate close of the popup with the same click as the popup opened.
  event?.stopPropagation();

  publish(OPEN_POPUP_EVENT, element.getAttribute("data-popup-id"));
  window.addEventListener("keydown", forceClose);

  const closePopupOnOutsideClick = element.getAttribute(
    "data-close-popup-on-outside-click",
  );
  if (closePopupOnOutsideClick !== "true") return;

  memoizedClickHandler = handleClickOutside(element);
  document.addEventListener("click", memoizedClickHandler, { capture: true });
};

/**
 * Handler to close the popup.
 */
export const closePopupHandler = (element, event) => {
  publish(CLOSE_POPUP_EVENT, element.getAttribute("data-popup-id"));
  window.removeEventListener("keydown", forceClose);
  document.removeEventListener("click", memoizedClickHandler, {
    capture: true,
  });
};

/**
 * Add subscriptions.
 */
export const enhancer = (element) => {
  const bodyElement = document.querySelector("body");

  subscribe(
    OPEN_POPUP_EVENT,
    updatePopupState(element, bodyElement, { open: true }),
  );
  subscribe(
    CLOSE_POPUP_EVENT,
    updatePopupState(element, bodyElement, { open: false }),
  );
  subscribe(
    FORCE_CLOSE_POPUP_EVENT,
    handleForceClosePopup(element, bodyElement),
  );
};
